import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import SocialLoginButtons from '../../components/Auth/SocialLoginButtons';

const Login = () => {
  const { login, loginWithPasscode } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  // Passwordless ("email me a code") mode.
  const [mode, setMode] = useState('password'); // 'password' | 'passcode'
  const [codeSent, setCodeSent] = useState(false);
  const [passcode, setPasscode] = useState('');

  // Shown when login fails because the email isn't verified yet -> offer a resend.
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);

  const resendVerification = async () => {
    if (!formData.email) { toast.error('Enter your email first.'); return; }
    setResending(true);
    try {
      await axios.post('/auth/resend-verification', { email: formData.email });
      toast.success('If an unverified account exists for that email, a new verification link is on its way. Check your inbox (and spam).');
    } catch (_) {
      // Generic response regardless (no account enumeration).
      toast.success('If an unverified account exists for that email, a new verification link is on its way. Check your inbox (and spam).');
    } finally {
      setResending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResend(false);

    const result = await login(formData.email, formData.password);

    setLoading(false);

    if (!result.success && result.code === 'EMAIL_NOT_VERIFIED') {
      // Offer a resend of the verification email (error toast already shown by context).
      setShowResend(true);
    }
  };

  // Request a one-time login code to be emailed.
  const requestCode = async () => {
    if (!formData.email) { toast.error('Enter your email first.'); return; }
    setLoading(true);
    try {
      await axios.post('/auth/request-passcode', { email: formData.email });
      setCodeSent(true);
      toast.success('If an account exists, a code is on its way.');
    } catch (_) {
      // Generic response; still advance so the user can enter a code.
      setCodeSent(true);
    } finally {
      setLoading(false);
    }
  };

  // Submit the emailed code to log in.
  const submitPasscode = async (e) => {
    e.preventDefault();
    setLoading(true);
    await loginWithPasscode(formData.email, passcode);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            
            {mode === 'password' && (
              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="form-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>

          {mode === 'password' && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex justify-center items-center"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>

              {/* Email-not-verified: offer to resend the verification email. */}
              {showResend && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  <p className="mb-2">
                    Your email isn’t verified yet. <span className="font-semibold">Check your spam / junk folder</span> —
                    our emails come from <span className="font-medium">info@gandalstock.com</span>. If it’s there, open it
                    and click <span className="font-semibold">“Not spam”</span>, then use the verification link. Or resend it below.
                  </p>
                  <button
                    type="button"
                    onClick={resendVerification}
                    disabled={resending}
                    className="inline-flex items-center gap-2 font-medium text-amber-900 underline hover:no-underline disabled:opacity-50"
                  >
                    {resending ? <LoadingSpinner size="sm" /> : null}
                    Resend verification email
                  </button>
                </div>
              )}

              {/* Passwordless: email me a one-time login code */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode('passcode'); setCodeSent(false); setPasscode(''); }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Email me a login code instead
                </button>
              </div>
            </>
          )}
        </form>

        {mode === 'passcode' && (
          <form className="mt-2 space-y-4 bg-white rounded-lg shadow-sm p-4" onSubmit={submitPasscode}>
            <p className="text-sm text-gray-600">
              {codeSent
                ? 'Enter the 6-digit code we emailed you. It expires in 10 minutes.'
                : "We'll email a one-time code to sign in without a password."}
            </p>
            {!codeSent ? (
              <button
                type="button"
                onClick={requestCode}
                disabled={loading}
                className="btn-primary w-full flex justify-center items-center"
              >
                {loading ? <LoadingSpinner size="sm" /> : 'Send me a code'}
              </button>
            ) : (
              <>
                <div>
                  <label htmlFor="passcode" className="form-label">Login code</label>
                  <input
                    id="passcode"
                    name="passcode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    className="form-input tracking-widest text-center text-lg"
                    placeholder="______"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex justify-center items-center">
                  {loading ? <LoadingSpinner size="sm" /> : 'Sign in with code'}
                </button>
                <button type="button" onClick={requestCode} disabled={loading} className="w-full text-sm text-blue-600 hover:text-blue-500">
                  Resend code
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setMode('password')}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Use password instead
            </button>
          </form>
        )}

        <SocialLoginButtons />
      </div>
    </div>
  );
};

export default Login;
